import { fromJS } from "immutable";
import cookie from "react-cookies";
import { getBrowserWidthAndHeight } from "../../common/shared";
import * as actionTypes from "../constants";
import * as menuStates from "../menuStates";
import {
  STANDARD_FONT,
  LIGHT_THEME,
  HIGHLIGHT_COLOR_LIGHT_BLUE,
} from "../constants";

const MINIMUM_WINDOW_WIDTH = 860;
const MINIMUM_WINDOW_HEIGHT = 525;

const MAXIMUM_WINDOW_HEIGHT = 725;
const MAXIMUM_WINDOW_WIDTH = 1180;

const MINIMUM_LEFT_VALUE = 220;
const MINIMUM_TOP_VALUE = 75;

const WINDOW_MARGIN = {
  x: 25,
  y: 50,
};

export const initialState = fromJS({
  serverError: {
    hasServerError: false,
    serverErrorMessage: "",
  },
  notifications: {
    showSuccessNotification: false,
    showDeleteNotification: false,
  },
  hasUnsavedContent: false,
  showSessionTimeoutModal: false,
  showSSOLogoutModal: false,
  showUserProfileModal: false,
  showClientWizard: false,
  clientListRefreshKey: Math.random(),
  menuState: menuStates.MENU_STATE_ADVISOR,
  sideMenuState: {},
  sideMenuStateLoaded: false,
  sideMenuCollapsed: false,
  showTrialCounter: false,
  trialCounterCreationDate: new Date(),
  showSecurityQuestionModal: false,
  windowParameters: {
    left: 220,
    top: 75,
    width: 1100,
    height: 750,
  },
  appearance: {
    font: STANDARD_FONT,
    displayMode: LIGHT_THEME,
    highlightColor: HIGHLIGHT_COLOR_LIGHT_BLUE,
  },
  isPlanCreationModalOpen: false,
  planCreationId: null,
  widgetsPositions: [],
  isPlanListDataValid: true,
});

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case actionTypes.SET_SERVER_ERROR:
      return state.setIn(["serverError", "hasServerError"], true);
    case actionTypes.CLEAR_SERVER_ERROR:
      return state.setIn(["serverError", "hasServerError"], false);
    case actionTypes.SHOW_SUCCESS_NOTIFICATION:
      return state.setIn(["notifications", "showSuccessNotification"], true);
    case actionTypes.CLEAR_SUCCESS_NOTIFICATION:
      return state.setIn(["notifications", "showSuccessNotification"], false);
    case actionTypes.SHOW_DELETE_NOTIFICATION:
      return state.setIn(["notifications", "showDeleteNotification"], true);
    case actionTypes.CLEAR_DELETE_NOTIFICATION:
      return state.setIn(["notifications", "showDeleteNotification"], false);
    case actionTypes.SET_HAS_EDITABLE_CONTENT:
      return state.setIn(["hasUnsavedContent"], action.data);
    case actionTypes.SHOW_TIMEOUT_MODAL:
      return state.setIn(["showSessionTimeoutModal"], true);
    case actionTypes.HIDE_TIMEOUT_MODAL:
      return state.setIn(["showSessionTimeoutModal"], false);
    case actionTypes.SHOW_SSO_MODAL:
      return state
        .setIn(["showSSOLogoutModal"], true)
        .setIn(["serverError", "hasServerError"], false);
    case actionTypes.HIDE_SSO_MODAL:
      return state.setIn(["showSSOLogoutModal"], false);
    case actionTypes.SET_MENU_STATE_ADMIN:
      return state.setIn(["menuState"], menuStates.MENU_STATE_ADMIN);
    case actionTypes.SET_MENU_STATE_ADVISOR:
      return state.setIn(["menuState"], menuStates.MENU_STATE_ADVISOR);
    case actionTypes.SET_MENU_STATE_SUPERADMIN:
      return state.setIn(["menuState"], menuStates.MENU_STATE_SUPERADMIN);
    case actionTypes.SET_MENU_STATE_SWITCH_USER:
      return state.setIn(["menuState"], menuStates.MENU_STATE_SWITCH_USER);
    case actionTypes.SET_SIDE_MENU_STATE:
      return state
        .setIn(["sideMenuState"], fromJS(action.data))
        .setIn(["sideMenuStateLoaded"], true);
    case actionTypes.SET_SIDE_MENU_COLLAPSED:
      let sideBarState = state.get("sideMenuState").toJS();

      Object.keys(sideBarState).forEach((item) => {
        sideBarState[item] = false;
      });

      return state
        .setIn(["sideMenuCollapsed"], true)
        .setIn(["sideMenuState"], fromJS(sideBarState));
    case actionTypes.SET_SIDE_MENU_EXPANDED:
      return state.setIn(["sideMenuCollapsed"], false);
    case actionTypes.SHOW_USER_PROFILE_MODAL:
      return state.setIn(["showUserProfileModal"], true);
    case actionTypes.HIDE_USER_PROFILE_MODAL:
      return state.setIn(["showUserProfileModal"], false);
    case actionTypes.SET_TRIAL_ACCOUNT:
      return state
        .setIn(["showTrialCounter"], true)
        .setIn(["trialCounterCreationDate"], action.firmCreationDate);
    case actionTypes.SHOW_SECURITY_QUESTION_MODAL:
      return state.setIn(["showSecurityQuestionModal"], true);
    case actionTypes.HIDE_SECURITY_QUESTION_MODAL:
      return state.setIn(["showSecurityQuestionModal"], false);
    case actionTypes.UPDATE_WINDOW_POSITION:
      const browserBoundary = getBrowserWidthAndHeight();
      const maxTop = browserBoundary.height - WINDOW_MARGIN.y;
      const maxLeft = browserBoundary.width - WINDOW_MARGIN.x;

      const top = Math.min(
        Math.max(action.data.top, MINIMUM_TOP_VALUE),
        maxTop
      );
      const left = Math.min(
        Math.max(action.data.left, MINIMUM_LEFT_VALUE),
        maxLeft
      );

      cookie.save(
        "instream_window",
        {
          top: top,
          left: left,
          width: state.getIn(["windowParameters", "width"]),
          height: state.getIn(["windowParameters", "height"]),
        },
        {
          path: "/",
        }
      );
      return state
        .setIn(["windowParameters", "top"], top)
        .setIn(["windowParameters", "left"], left);
    case actionTypes.UPDATE_WINDOW_SIZE:
      const width = Math.min(
        Math.max(action.data.width, MINIMUM_WINDOW_WIDTH),
        MAXIMUM_WINDOW_WIDTH
      );
      const height = Math.min(
        Math.max(action.data.height, MINIMUM_WINDOW_HEIGHT),
        MAXIMUM_WINDOW_HEIGHT
      );

      cookie.save(
        "instream_window",
        {
          top: state.getIn(["windowParameters", "top"]),
          left: state.getIn(["windowParameters", "left"]),
          width: width,
          height: height,
        },
        {
          path: "/",
        }
      );
      return state
        .setIn(["windowParameters", "width"], width)
        .setIn(["windowParameters", "height"], height);

    case actionTypes.UPDATE_FONT:
      return state.setIn(["appearance", "font"], action.value);
    case actionTypes.UPDATE_DISPLAY_MODE:
      return state.setIn(["appearance", "displayMode"], action.value);
    case actionTypes.UPDATE_HIGHLIGHT_COLOR:
      return state.setIn(["appearance", "highlightColor"], action.value);
    case actionTypes.SET_CLIENT_WIZARD_VISIBILITY:
      return state.setIn(["showClientWizard"], action.value);
    case actionTypes.TRIGGER_CLIENT_LIST_REFRESH:
      return state.setIn(["clientListRefreshKey"], Math.random());
    case actionTypes.SHOW_PLAN_CREATION_MODAL:
      return state
        .setIn(["isPlanCreationModalOpen"], true)
        .setIn(["planCreationId"], action.value);
    case actionTypes.UPDATE_PLAN_CREATION_ID:
      return state.setIn(["planCreationId"], action.value);
    case actionTypes.HIDE_PLAN_CREATION_MODAL:
      return state.setIn(["isPlanCreationModalOpen"], false);
    case actionTypes.SET_WIDGET_DYNAMIC_DASHBOARD:
      return state.setIn(["widgetsPositions"], action.data);
    case actionTypes.MARK_PLAN_LIST_VALID:
      return state.setIn(["isPlanListDataValid"], true);
    case actionTypes.MARK_PLAN_LIST_INVALID:
      return state.setIn(["isPlanListDataValid"], false);
    default:
      return state;
  }
}
